import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../../../stores/auth.store';
import { Icon } from '../../../components/ui/Icon';
import { AnimatedNumber } from '../../../components/ui/AnimatedNumber';
import { SkeletonKPIRow } from '../../../components/ui/Skeleton';
import { BANCA_VISTE, type BancaVista } from '../../../lib/adminRoutes';
import { canWriteAdmin } from '../adminApi';
import { RiconciliazioneTab } from '../RiconciliazioneTab';
import { MovimentiTab } from '../MovimentiTab';
import { ChiuseQuiTab } from '../ChiuseQuiTab';
import { AdminHeader, StoricoNote, ViewTabs, useStorico, useSyncAll, useVista, type ViewTab } from '../shared/AdminShell';
import { bancaSintesiQuery, fmtSaldo, parseEuro, quandoAggiornata, useBancaActions, type BancaSintesi } from './bancaApi';
import { BancaMobile } from './BancaMobile';
import { useIsMobile } from '../../../hooks/useIsMobile';
import '../admin.css';
import './banca.css';

/**
 * Banca (`/admin/banca`) — cosa è passato sul conto, e quali bonifici vanno
 * ancora abbinati alle fatture.
 *
 *   intestazione  la frase calcolata: quanti bonifici tornano al centesimo con
 *                 una fattura, quanti vanno controllati, a che ora è la banca
 *   fascia        i singoli conti (principale, IVA, contanti, gli altri). Il
 *                 TOTALE non c'è: vive nella Situazione ("Soldi disponibili
 *                 oggi"), qui si vedono le parti (regola R2)
 *   viste         Bonifici da abbinare (predefinita) · Movimenti del conto ·
 *                 Da comunicare al commercialista, con il pallino dove c'è
 *                 qualcosa da fare
 *
 * Le chiavi di `?vista=` restano quelle dello scheletro (controllare,
 * movimenti, commercialista): cambiano solo le parole a schermo. Il filtro
 * "Anche prima del 2026" vale solo in Movimenti del conto.
 */
/**
 * Sul telefono la pagina è BancaMobile (l'estratto conto: un gruppo per giorno,
 * i gesti nel pannello del movimento). Sul computer resta questa.
 */
export function BancaPage() {
  const isMobile = useIsMobile();
  return isMobile ? <BancaMobile /> : <BancaDesktop />;
}

function BancaDesktop() {
  const user = useAuthStore((s) => s.user);
  const canWrite = canWriteAdmin(user);
  const [vista, setVista] = useVista<BancaVista>(BANCA_VISTE);
  const storico = useStorico();
  const sync = useSyncAll();
  const conStorico = vista === 'movimenti';
  const { data: s, isLoading } = useQuery(bancaSintesiQuery);

  // Regola delle etichette (24/9): ogni linguetta dice di cosa si parla, da sola.
  const tabs: ViewTab<BancaVista>[] = [
    {
      key: 'controllare',
      label: 'Bonifici da abbinare',
      count: s ? s.bonifici.sicuri + s.bonifici.incerti : null,
      title: 'Bonifici in banca che somigliano a una fattura non ancora pagata',
    },
    { key: 'movimenti', label: 'Movimenti del conto', title: 'L’estratto conto di Qonto, un movimento per riga' },
    {
      key: 'commercialista',
      label: 'Da comunicare al commercialista',
      count: s?.commercialista ?? null,
      title: 'Pagamenti segnati qui e ancora aperti su FattureInCloud',
    },
  ];

  return (
    <div className="page adm-page">
      <AdminHeader
        title="Banca"
        sub={<Frase s={s} />}
        actions={<>{conStorico && storico.button}{sync.button}</>}
        menu={[...(conStorico ? [storico.menuItem] : []), sync.menuItem]}
      />

      {isLoading && !s ? <SkeletonKPIRow count={4} withIcon={false} /> : s && <FasciaConti s={s} canWrite={canWrite} />}

      {conStorico && storico.on && <StoricoNote year={storico.year} onOff={() => storico.set(false)} />}

      <ViewTabs tabs={tabs} active={vista} onChange={setVista} />

      {vista === 'controllare' && <RiconciliazioneTab canWrite={canWrite} />}
      {vista === 'movimenti' && <MovimentiTab canWrite={canWrite} showHistory={storico.on} />}
      {vista === 'commercialista' && <ChiuseQuiTab />}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// La frase in cima (R6): solo conteggi e un'ora, mai importi (R1)
// ════════════════════════════════════════════════════════════════════

/**
 * "Con i dati di oggi 1 pagamento in banca torna al centesimo con una fattura:
 * basta confermarlo. Altri 2 tornano solo in parte e vanno controllati. Banca
 * aggiornata oggi alle 10:45."
 *
 * Sta in uno <span> e non come testo nudo: sul telefono l'intestazione tiene
 * solo i figli elemento (mobile.md 4bis.3), e questa frase è un fatto, non prosa.
 */
function Frase({ s }: { s: BancaSintesi | undefined }) {
  if (!s) return null;
  if (!s.qontoCollegato) {
    return <span className="text-warn">Qonto non è collegato: senza, qui non arrivano né i saldi né i movimenti. Si collega dalle Impostazioni.</span>;
  }
  const { sicuri, incerti } = s.bonifici;
  const uno = (n: number, a: string, b: string) => (n === 1 ? a : b);
  let testo: string;
  if (sicuri > 0) {
    testo = `Con i dati di oggi ${sicuri} ${uno(sicuri, 'pagamento', 'pagamenti')} in banca ${uno(sicuri, 'torna', 'tornano')} al centesimo con una fattura: basta ${uno(sicuri, 'confermarlo', 'confermarli')}.`;
    if (incerti > 0) {
      testo += incerti === 1
        ? ' Un altro torna solo in parte e va controllato.'
        : ` Altri ${incerti} tornano solo in parte e vanno controllati.`;
    }
  } else if (incerti > 0) {
    testo = `Con i dati di oggi nessun pagamento in banca torna al centesimo con una fattura; ${incerti} ${uno(incerti, 'torna', 'tornano')} solo in parte e ${uno(incerti, 'va controllato', 'vanno controllati')}.`;
  } else {
    testo = 'Con i dati di oggi ogni pagamento in banca che somiglia a una fattura è già collegato: non ci sono bonifici da abbinare.';
  }
  // Il numero sta nel blocco in cima alla vista: qui solo che è successo (R1).
  if ((s.automatici ?? 0) > 0) testo += ' I pagamenti che tornavano del tutto si sono segnati da soli: li trovi in cima all’elenco.';
  if (s.aggiornataAlle) testo += ` Banca aggiornata ${quandoAggiornata(s.aggiornataAlle)}.`;
  return <span className="bn-frase">{testo}</span>;
}

// ════════════════════════════════════════════════════════════════════
// La fascia dei conti: le parti, mai il totale
// ════════════════════════════════════════════════════════════════════

function FasciaConti({ s, canWrite }: { s: BancaSintesi; canWrite: boolean }) {
  return (
    <div className="stat-band bn-conti">
      {s.principale && (
        <Cella
          label="Conto principale"
          value={s.principale.saldo}
          sub={s.principale.ibanFinale ? `Qonto, l’IBAN finisce con ${s.principale.ibanFinale}` : 'Qonto'}
        />
      )}
      {s.iva && <Cella label={`Conto ${s.iva.nome}`} value={s.iva.saldo} sub="Qonto, i soldi messi da parte per l’IVA" />}
      {s.contanti && <CellaContanti saldo={s.contanti.saldo} canWrite={canWrite} />}
      {s.altri.nomi.length > 0 && (
        <Cella label="Altri conti Qonto" value={s.altri.saldo} sub={elenco(s.altri.nomi)} />
      )}
    </div>
  );
}

/** "GoDeal e Salvadanaio", "A, B e C". */
function elenco(nomi: string[]): string {
  if (nomi.length <= 1) return nomi[0] ?? '';
  return `${nomi.slice(0, -1).join(', ')} e ${nomi[nomi.length - 1]}`;
}

function Cella({ label, value, sub }: { label: string; value: number; sub: string }) {
  return (
    <div>
      <div className="st-label">{label}</div>
      <div className="st-value"><span className="u">€</span><AnimatedNumber value={value} format={fmtSaldo} /></div>
      <div className="st-delta">{sub}</div>
    </div>
  );
}

/**
 * I contanti non arrivano da nessuna banca: si contano a mano. La matita apre
 * il campo al posto della cifra; si scrive quanto c'è in cassa OGGI e l'API
 * sposta il saldo di partenza di quanto serve (admin.banca.routes.ts).
 */
function CellaContanti({ saldo, canWrite }: { saldo: number; canWrite: boolean }) {
  const { correggiContanti } = useBancaActions();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const apri = () => { setValue(String(saldo).replace('.', ',')); setEditing(true); };
  const salva = () => {
    const n = parseEuro(value);
    if (!Number.isFinite(n)) return;
    setEditing(false);
    if (Math.round(n * 100) !== Math.round(saldo * 100)) correggiContanti.mutate(n);
  };
  return (
    <div>
      <div className="st-label">Contanti in cassa</div>
      {editing ? (
        <div className="bn-cassa-edit">
          <input
            className="inp inp-sm mono"
            inputMode="decimal"
            autoFocus
            aria-label="Contanti in cassa oggi, in euro"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') salva(); if (e.key === 'Escape') setEditing(false); }}
          />
          <button className="btn btn-sm btn-primary btn-icon" aria-label="Salva i contanti" title="Salva i contanti" onClick={salva}>
            <Icon name="check" size={12} />
          </button>
          <button className="btn btn-sm btn-ghost btn-icon" aria-label="Lascia i contanti come sono" title="Lascia i contanti come sono" onClick={() => setEditing(false)}>
            <Icon name="x" size={12} />
          </button>
        </div>
      ) : (
        <div className="st-value"><span className="u">€</span><AnimatedNumber value={saldo} format={fmtSaldo} /></div>
      )}
      <div className="st-delta">
        {canWrite && !editing ? (
          <button className="btn btn-sm btn-ghost" disabled={correggiContanti.isPending} onClick={apri} title="Scrivi quanti contanti ci sono in cassa oggi">
            <Icon name="pencil" size={10} /> Correggi i contanti
          </button>
        ) : editing ? 'quanti ce ne sono oggi, contati a mano' : 'si contano a mano, non da una banca'}
      </div>
    </div>
  );
}
